import React, { useEffect } from 'react';
import {
    Box,
    Heading,
    Text,
    Button,
    VStack,
    Grid,
    GridItem,
} from '@chakra-ui/react';
import { useColorModeValue } from '../../components/ui/color-mode';
import { darkMode, lightMode } from '../../theme/config';
import { useForm } from 'react-hook-form';
import { InputBox, SelectBox } from '../../components/shared/fields';
import { useDispatch, useSelector } from 'react-redux';
import { clearUserState, createUserRequest } from '../../store/userSlice';
import { showAlert } from '../../store/alertSlice';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ContactBox from '../../components/shared/fields/ContactBox';
import { resetCustomerData, setSearchValue } from '../../store/customerSlice';
import { parsePhoneNumber } from 'libphonenumber-js';

const PermissionsIndex = () => {

    const dispatch = useDispatch();
    const textColor = useColorModeValue(lightMode.textColor, darkMode.textColor);
    const redStar = <span style={{ color: "red" }}>*</span>;
    const { message, errMsg, error } = useSelector((state) => state.user || {
        message: undefined,
        errMsg: undefined,
        error: null,
    });

    useEffect(() => {
        if (message) {
            dispatch(showAlert({
                message: message,
                type: "success",
            }));
        } else if (errMsg) {
            dispatch(showAlert({
                message: errMsg,
                type: "error",
            }));
        } else if (error) {
            dispatch(showAlert({
                message: "Something went wrong",
                type: "error",
            }));
        }
        dispatch(clearUserState());
        dispatch(resetCustomerData());
        dispatch(setSearchValue(''));
    }, [message, errMsg, error]);

    const schema = z.object({
        firstName: z.string().min(1, "First Name is required").max(30, "First Name must be at most 30 characters"),
        lastName: z.string().min(1, "Last Name is required").max(30, "Last Name must be at most 30 characters"),
        phoneNumber: z.string().regex(/^\+?\d{7,15}$/, "Invalid phone number"),
        email: z.string().email("Invalid email address"),
        role: z.array(z.string()).optional(),
    });

    const { control, handleSubmit, reset, formState: { errors }, register } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: '',
            role: [],
        },
        resolver: zodResolver(schema),
    });

    const roleOptions = [
        { value: 'support', label: 'Support' },
        { value: 'others', label: 'Others' },
    ];


    const handleAddExecutive = (data) => {
        let countryCode = "", nationalNumber = "";
        if (data.phoneNumber) {
            const parsed = parsePhoneNumber(data.phoneNumber);
            countryCode = "+" + parsed.countryCallingCode;
            nationalNumber = parsed.nationalNumber;
        }
        dispatch(createUserRequest({
            ...data,
            localPhoneNo: nationalNumber,
            source: 'permissions',
            phoneNumber: data.phoneNumber,
            mobileCountryCode: countryCode
        }));
        reset();
    }

    return (
        <Box p={4} color={textColor}>
            <Heading size="3xl" mb={4} color={textColor} fontWeight="bold">
                Executive Management
            </Heading>

            <Box as={"form"} onSubmit={handleSubmit(handleAddExecutive)} p={6} borderRadius="md" boxShadow="md" mx="auto">
                <Heading size="xl" fontWeight="bold" mb={2}>
                    Add New Executive
                </Heading>
                <Text fontSize="sm" color="gray.500" mb={6}>
                    First-time login requires using 'Forgot Password'
                </Text>

                <VStack spacing={6} align="stretch">
                    <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }} gap={4}>
                        <GridItem>
                            <InputBox
                                label={<>{"First Name"} {redStar}</>}
                                placeholder="First Name"
                                invalid={!!errors.firstName}
                                errorText={errors.firstName?.message}
                                register={{ ...register('firstName') }}
                            />
                        </GridItem>
                        <GridItem>
                            <InputBox
                                label={<>{"Last Name"} {redStar}</>}
                                placeholder="Last Name"
                                invalid={!!errors.lastName}
                                errorText={errors.lastName?.message}
                                register={{ ...register('lastName') }}
                            />
                        </GridItem>
                    </Grid>

                    <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }} gap={4}>
                        <GridItem>
                            <ContactBox
                                label={<>{"Phone Number"} {redStar}</>}
                                invalid={!!errors.phoneNumber}
                                errorText={errors.phoneNumber?.message}
                                register={{ ...register('phoneNumber') }}
                            />
                        </GridItem>
                    </Grid>

                    {/* <Grid templateColumns="2fr 1fr 1fr" gap={2}> */}
                    <Grid templateColumns={{ md: "1fr 1fr 1fr", lg: "2fr 1fr 1fr" }} gap={2}>
                        <GridItem>
                            <InputBox
                                label={<>{"Email"} {redStar}</>}
                                placeholder="Email"
                                invalid={!!errors.email}
                                errorText={errors.email?.message}
                                register={{ ...register('email') }}
                            />
                        </GridItem>
                        <GridItem marginLeft={1}>
                            <SelectBox
                                label="Role"
                                name="role"
                                control={control}
                                items={roleOptions}
                                placeholder="Select Role"
                            />
                        </GridItem>
                        <GridItem style={{ display: "flex", alignItems: "center" }}>
                            <Button
                                w="100%"
                                colorPalette="blue"
                                type='submit'
                            >
                                Add Executive
                            </Button>
                        </GridItem>
                    </Grid>
                </VStack>
            </Box>
        </Box>
    );


};

export default PermissionsIndex;
